import { Navigate, Route, Routes } from 'react-router';

import { ProtectedRoute } from '@/components/AppRouter/ProtectedRoute';
import { UserLocaleSetup } from '@/components/AppRouter/UserLocaleSetup';
import { SignOutDrawer } from '@/components/Layout/Drawer/SignOutDrawer';
import { UserInfoDrawer } from '@/components/Layout/Drawer/UserInfoDrawer';

import { ROUTE } from '@/constants/routes';

import { useAppSelector } from '@/hooks/redux';

import BasicInfoScreen from '@/pages/account/basic-information/BasicInfoPage';
import ComingSoonPage from '@/pages/application-type/ComingSoonPage';
import AddAdultContactScreen from '@/pages/application-type/mobile/AddAdultContactScreen';
import AddAdultDetailResultFailedScreen from '@/pages/application-type/mobile/AddAdultDetailResultFailedScreen';
import AddAdultDetailResultScreen from '@/pages/application-type/mobile/AddAdultDetailResultScreen';
import AddAdultDetailScreen from '@/pages/application-type/mobile/AddAdultDetailScreen';
import RightStartComingSoonPage from '@/pages/application-type/RightStartComingSoonPage';
import MinorAgeValidationFailedPage from '@/pages/onboarding/minor-age-validation';
import NonResidentMinorRejectedPage from '@/pages/onboarding/non-resident-minor-rejected/NonResidentMinorRejectedPage';
import FinishedScreen from '@/pages/onboarding/success/FinishedPage';
import ReviewPersonalDetailsPage from '@/pages/personal-details/ReviewPersonalDetailsPage';
import UpdatePersonalInfoPage from '@/pages/personal-details/UpdatePersonalInfoPage';

import { selectAuthState } from '@/redux/auth/selectors';
import {
  selectNewApplicationTypeSelectionFeature,
  selectRightStartFeature,
} from '@/redux/configs/selectors';
import { selectUIState } from '@/redux/ui/selectors';

import { DocumentType } from '@/types/document';
import { ApplicationType } from '@/types/user';

import AccountTypeScreen from '../../account-type/mobile/AccountTypeScreen';
import ApplicationTypeSelectionScreen from '../../application-type/ApplicationTypeSelectionPage';
import ApplicationTypeSelectionFailedScreen from '../../application-type/mobile/ApplicationTypeSelectionFailedScreen';
import AgeValidationFailedScreen from '../../onboarding/age-validation/AgeValidationFailedPage';
import CaymanCountryValidationFailedScreen from '../../onboarding/cayman-country-resident-validation/CaymanCountryValidationFailedPage';
import CaymanThirdPartyComplianceCheckFailedScreen from '../../third-party-compliance-check/mobile/CaymanThirdPartyComplianceCheckFailedScreen';
import ThirdPartyComplianceCheckFailedScreen from '../../third-party-compliance-check/mobile/ThirdPartyComplianceCheckFailedScreen';
import ThirdPartyComplianceCheckScreen from '../../third-party-compliance-check/mobile/ThirdPartyComplianceCheckScreen';
import AccountRejectedScreen from '../account-status/AccountRejectedScreen';
import AddressScreen from '../create-new-account/AddressScreen';
import JointAdultAgeValidationFailedScreen from '../create-new-account/JointAdultAgeValidationFailedScreen';
import MinorBasicInfoScreen from '../create-new-account/MinorBasicInfoScreen';
import MoreInformationScreen from '../more-information/MoreInformationScreen';
import PhoneScreen from '../phone/PhoneScreen';
import MinorReviewCompleteScreen from '../verification/MinorReviewCompleteScreen';
import VerifyIDScreen from '../verification/VerifyIDScreen';
import VerifyMinorBirthCertificateScreen from '../verification/VerifyMinorBirthCertificateScreen';
import VerifyMinorIDScreen from '../verification/VerifyMinorIDScreen';
import VerifySelfieScreen from '../verification/VerifySelfieScreen';

export function AuthenticatedScreen() {
  const enabledRightStartAccount = useAppSelector(selectRightStartFeature);
  const enabledNewApplicationTypeSelection = useAppSelector(
    selectNewApplicationTypeSelectionFeature,
  );
  const { isSignOutConfirming } = useAppSelector(selectAuthState);
  const { isShowingUserInfo } = useAppSelector(selectUIState);

  return (
    <UserLocaleSetup>
      <Routes>
        <Route path={ROUTE.HOME} element={<ProtectedRoute />} />
        <Route
          path={ROUTE.PHONE_FORM}
          element={
            <ProtectedRoute>
              <PhoneScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.VERIFY_ID}
          element={
            <ProtectedRoute>
              <VerifyIDScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.VERIFY_MINOR_ID}
          element={
            <ProtectedRoute>
              <VerifyMinorIDScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.VERIFY_SELFIE}
          element={
            <ProtectedRoute>
              <VerifySelfieScreen />
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTE.VERIFY_MINOR_BIRTH_CERTIFICATE}
          element={
            <ProtectedRoute>
              <VerifyMinorBirthCertificateScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.VERIFY_MINOR_FAMILY_BOOK}
          element={
            <ProtectedRoute>
              <VerifyMinorBirthCertificateScreen
                documentType={DocumentType.FamilyBook}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.MINOR_REVIEW_COMPLETE}
          element={
            <ProtectedRoute>
              <MinorReviewCompleteScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.MINOR_BASIC_INFO}
          element={
            <ProtectedRoute>
              <MinorBasicInfoScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.BASIC_INFO}
          element={
            <ProtectedRoute>
              <BasicInfoScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.ADDRESS_INFORMATION}
          element={
            <ProtectedRoute>
              <AddressScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.SUCCESS}
          element={
            <ProtectedRoute>
              <FinishedScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.ACCOUNT_TYPE}
          element={
            <ProtectedRoute>
              <AccountTypeScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.ACCOUNT_REJECTED}
          element={<AccountRejectedScreen />}
        />
        <Route
          path={ROUTE.MORE_INFORMATION}
          element={
            <ProtectedRoute>
              <MoreInformationScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.REVIEW_PERSONAL_DETAILS}
          element={
            <ProtectedRoute>
              <ReviewPersonalDetailsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.UPDATE_PERSONAL_DETAILS}
          element={
            <ProtectedRoute>
              <UpdatePersonalInfoPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.AGE_VALIDATION_FAILURE}
          element={<AgeValidationFailedScreen />}
        />
        <Route
          path={ROUTE.THIRD_PARTY_COMPLIANCE_CHECK}
          element={
            <ProtectedRoute>
              <ThirdPartyComplianceCheckScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTE.THIRD_PARTY_COMPLIANCE_CHECK_FAILED}
          element={<ThirdPartyComplianceCheckFailedScreen />}
        />
        <Route
          path={ROUTE.CAYMAN_COUNTRY_VALIDATION_FAILURE}
          element={<CaymanCountryValidationFailedScreen />}
        />
        <Route
          path={ROUTE.CAYMAN_THIRD_PARTY_COMPLIANCE_CHECK_FAILED}
          element={
            <ProtectedRoute>
              <CaymanThirdPartyComplianceCheckFailedScreen />
            </ProtectedRoute>
          }
        />
        {enabledNewApplicationTypeSelection && (
          <>
            <Route
              path={ROUTE.APPLICATION_TYPE_SELECTION}
              element={
                <ProtectedRoute>
                  <ApplicationTypeSelectionScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTE.APPLICATION_TYPE_SELECTION_FAILED}
              element={<ApplicationTypeSelectionFailedScreen />}
            />
            <Route
              path={ROUTE.RIGHT_START_COMING_SOON}
              element={
                <ProtectedRoute>
                  <RightStartComingSoonPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTE.BUSINESS_COMING_SOON}
              element={
                <ProtectedRoute>
                  <ComingSoonPage type={ApplicationType.BUSINESS} />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTE.SHARED_WITH_SOMEONE_ELSE_COMING_SOON}
              element={
                <ProtectedRoute>
                  <ComingSoonPage
                    type={ApplicationType.SHARED_WITH_SOMEONE_ELSE}
                  />
                </ProtectedRoute>
              }
            />
          </>
        )}
        {enabledRightStartAccount && (
          <>
            <Route
              path={ROUTE.JOINT_ADULT_AGE_VALIDATION_FAILURE}
              element={<JointAdultAgeValidationFailedScreen />}
            />
            <Route
              path={ROUTE.MINOR_AGE_VALIDATION_FAILURE}
              element={<MinorAgeValidationFailedPage />}
            />
            <Route
              path={ROUTE.NON_RESIDENT_MINOR_REJECTED}
              element={<NonResidentMinorRejectedPage />}
            />
            <Route
              path={ROUTE.ADD_ADULT_DETAIL}
              element={
                <ProtectedRoute>
                  <AddAdultDetailScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTE.ADD_ADULT_DETAIL_RESULT_FAILED}
              element={<AddAdultDetailResultFailedScreen />}
            />
            <Route
              path={ROUTE.ADD_ADULT_DETAIL_RESULT}
              element={
                <ProtectedRoute>
                  <AddAdultDetailResultScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTE.ADD_ADULT_CONTACT}
              element={
                <ProtectedRoute>
                  <AddAdultContactScreen />
                </ProtectedRoute>
              }
            />
          </>
        )}
        <Route path="*" element={<Navigate to={ROUTE.HOME} />} />
      </Routes>
      {isSignOutConfirming && <SignOutDrawer />}
      {isShowingUserInfo && <UserInfoDrawer />}
    </UserLocaleSetup>
  );
}
